import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RAG_META } from "@/lib/rag";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Users, ListChecks, Network, BarChart3, Copy, GripVertical, Pencil, FileText, Download, ScrollText, ShieldAlert, UserPlus, CalendarClock, Mail, SeparatorHorizontal, Check, ClipboardList, MapPin, Wrench, ArrowUp, ArrowDown, Archive } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import OrganizationTab from "@/pages/admin/OrganizationTab";
import MembersScoresTab from "@/pages/admin/MembersScoresTab";
import UserLogTab from "@/pages/admin/UserLogTab";
import AdminExportTab from "@/pages/admin/AdminExportTab";
import ExportLogTab from "@/pages/admin/ExportLogTab";
import SecurityLogTab from "@/pages/admin/SecurityLogTab";
import InviteTab from "@/pages/admin/InviteTab";
import DigestTab from "@/pages/admin/DigestTab";
import DutyTemplatesTab from "@/pages/admin/DutyTemplatesTab";
import TaskConfigTab from "@/pages/admin/TaskConfigTab";
import LocationsTab from "@/pages/admin/LocationsTab";
import EquipmentTab from "@/pages/admin/EquipmentTab";
import BackupTab from "@/pages/admin/BackupTab";

export default function AdminPage() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const baseTabs = isSuper ? [
    { id: "companies", label: "Companies", icon: Building2 },
    { id: "roles", label: "Roles", icon: Users },
    { id: "options", label: "Question Options", icon: ListChecks },
  ] : [];
  const canExport = isSuper || user?.admin_level === "company";
  const canInvite = isSuper || ["company", "unit"].includes(user?.admin_level);
  const tabs = [
    ...baseTabs,
    { id: "organization", label: "Organization", icon: Network },
    { id: "members", label: "Members", icon: BarChart3 },
    ...(canInvite ? [{ id: "invites", label: "Invite Users", icon: UserPlus }] : []),
    ...(canInvite ? [{ id: "duty", label: "Duty Templates", icon: CalendarClock }] : []),
    ...(canInvite ? [{ id: "tasks", label: `${user?.terminology?.task_label || "Task"} Config`, icon: ClipboardList }] : []),
    ...(canInvite ? [{ id: "equipment", label: "Equipment", icon: Wrench }] : []),
    ...(canInvite ? [{ id: "locations", label: "Locations", icon: MapPin }] : []),
    { id: "userlog", label: "User Log", icon: FileText },
    ...(canExport ? [
      { id: "export", label: "Export", icon: Download },
      { id: "backup", label: "Backup", icon: Archive },
      { id: "exportlog", label: "Export Log", icon: ScrollText },
      { id: "security", label: "Security Log", icon: ShieldAlert },
      { id: "digest", label: "Digest Emails", icon: Mail },
    ] : []),
  ];
  const [tab, setTab] = useState(isSuper ? "companies" : "organization");
  if (user && user.admin_mode === false) {
    return (
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 text-sm text-slate-400" data-testid="admin-mode-off">
        Admin mode is off. Turn it back on from the sidebar to manage settings.
      </div>
    );
  }
  return (
    <div>
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Administration</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Command Center</h1>
        <p className="text-slate-400 text-sm mt-1">Manage companies, crew roles, and configurable RAG-scored options per role.</p>
      </div>
      <div className="flex flex-wrap gap-x-2 gap-y-1 mb-6 border-b border-slate-800">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 text-sm transition-colors shrink-0 whitespace-nowrap ${
                active ? "bg-blue-600 border-blue-600 text-white rounded-t-md" : "border-transparent text-slate-400 hover:text-white"
              }`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "companies" && <CompaniesTab />}
      {tab === "roles" && <RolesTab />}
      {tab === "options" && <OptionsTab />}
      {tab === "organization" && <OrganizationTab />}
      {tab === "members" && <MembersScoresTab />}
      {tab === "invites" && <InviteTab />}
      {tab === "duty" && <DutyTemplatesTab />}
      {tab === "tasks" && <TaskConfigTab />}
      {tab === "equipment" && <EquipmentTab />}
      {tab === "locations" && <LocationsTab />}
      {tab === "userlog" && <UserLogTab />}
      {tab === "export" && <AdminExportTab />}
      {tab === "backup" && <BackupTab />}
      {tab === "exportlog" && <ExportLogTab />}
      {tab === "security" && <SecurityLogTab />}
      {tab === "digest" && <DigestTab />}
    </div>
  );
}

function CompaniesTab() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/companies").then((r) => setList(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/admin/companies", { name: name.trim() }); setName(""); load(); toast.success("Company added"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const rename = async (id) => {
    const n = prompt("New company name?");
    if (n) { await api.put(`/admin/companies/${id}`, { name: n }); load(); }
  };
  const [labelsFor, setLabelsFor] = useState(null);
  const [labelDraft, setLabelDraft] = useState(null);
  const openLabels = async (c) => {
    if (labelsFor === c.id) { setLabelsFor(null); return; }
    const { data } = await api.get(`/admin/companies/${c.id}/terminology`);
    setLabelDraft({ ...data });
    setLabelsFor(c.id);
  };
  const saveLabels = async (c) => {
    try {
      await api.put(`/admin/companies/${c.id}/terminology`, labelDraft);
      toast.success(`Terminology saved for ${c.name}`);
      setLabelsFor(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [myPageFor, setMyPageFor] = useState(null);
  const [myPageDraft, setMyPageDraft] = useState(null);
  const openMyPage = async (c) => {
    if (myPageFor === c.id) { setMyPageFor(null); return; }
    try {
      const { data } = await api.get(`/admin/companies/${c.id}/my-page`);
      setMyPageDraft({ ...data, sections: { ...(data.sections || {}) } });
      setMyPageFor(c.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveMyPage = async (c) => {
    try {
      await api.put(`/admin/companies/${c.id}/my-page`, myPageDraft);
      toast.success(`My Page saved for ${c.name}`);
      setMyPageFor(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Delete this company and all its roles/options?")) { await api.delete(`/admin/companies/${id}`); load(); } };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <div className="font-heading text-lg font-semibold mb-3">Add Company</div>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-new-company-name"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm focus:border-blue-500 outline-none"
          placeholder="Company name" />
        <button onClick={add} data-testid="admin-add-company-btn"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="md:col-span-2 p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <div className="font-heading text-lg font-semibold mb-3">Companies</div>
        <div className="divide-y divide-slate-800">
          {list.length === 0 && <div className="text-sm text-slate-500">No companies yet.</div>}
          {list.map((c) => (
            <div key={c.id} className="py-2.5" data-testid={`admin-company-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm">{c.name}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openLabels(c)} data-testid={`company-labels-${c.id}`} className="text-xs text-slate-300 hover:text-white">Labels</button>
                  <button onClick={() => openMyPage(c)} data-testid={`company-my-page-${c.id}`} className="text-xs text-slate-300 hover:text-white">My Page</button>
                  <button onClick={() => rename(c.id)} className="text-xs text-blue-400 hover:text-blue-300">Rename</button>
                  <button onClick={() => del(c.id)} className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
              </div>
              {labelsFor === c.id && labelDraft && (
                <div className="mt-2 flex items-end gap-2 flex-wrap" data-testid={`company-labels-panel-${c.id}`}>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Task label (singular)</label>
                    <input value={labelDraft.task_label || ""} onChange={(e) => setLabelDraft({ ...labelDraft, task_label: e.target.value })}
                      data-testid={`company-task-label-${c.id}`} placeholder="Task"
                      className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Task label (plural)</label>
                    <input value={labelDraft.task_label_plural || ""} onChange={(e) => setLabelDraft({ ...labelDraft, task_label_plural: e.target.value })}
                      data-testid={`company-task-label-plural-${c.id}`} placeholder="Tasks"
                      className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm" />
                  </div>
                  <button onClick={() => saveLabels(c)} data-testid={`company-labels-save-${c.id}`}
                    className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium">Save</button>
                </div>
              )}
              {myPageFor === c.id && myPageDraft && (
                <div className="mt-2 p-3 rounded-md border border-slate-800 bg-slate-950/40" data-testid={`company-my-page-panel-${c.id}`}>
                  <label className="flex items-center gap-2 text-xs text-slate-300 mb-2">
                    <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={myPageDraft.enabled !== false}
                      data-testid={`company-my-page-enabled-${c.id}`}
                      onChange={(e) => setMyPageDraft({ ...myPageDraft, enabled: e.target.checked })} />
                    Enabled — members see it under the Profile menu
                  </label>
                  <div className="flex items-end gap-2 flex-wrap">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">Page name</label>
                      <input value={myPageDraft.name || ""} onChange={(e) => setMyPageDraft({ ...myPageDraft, name: e.target.value })}
                        data-testid={`company-my-page-name-${c.id}`} placeholder="My Page"
                        className="px-3 py-2 rounded-md bg-slate-950 border border-slate-800 text-sm" />
                    </div>
                    <button onClick={() => saveMyPage(c)} data-testid={`company-my-page-save-${c.id}`}
                      className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium">Save</button>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mt-3 mb-1.5">Sections shown</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {[["details", "Member details"], ["fatigue7", "Fatigue — last 7 days"], ["plan7", "Next 7 days plan"], ["post_task", "Post-task list"], ["auths", "Awaiting authorisation"]].map(([k, lbl]) => (
                      <label key={k} className="flex items-center gap-1.5 text-xs text-slate-300">
                        <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={myPageDraft.sections?.[k] !== false}
                          data-testid={`company-my-page-sec-${k}-${c.id}`}
                          onChange={(e) => setMyPageDraft({ ...myPageDraft, sections: { ...myPageDraft.sections, [k]: e.target.checked } })} />
                        {lbl}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RolesTab() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState("");
  const [weight, setWeight] = useState("1");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editWeight, setEditWeight] = useState("1");
  useEffect(() => { api.get("/companies").then((r) => { setCompanies(r.data); if (r.data[0]) setCompanyId(r.data[0].id); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (companyId) api.get(`/roles?company_id=${companyId}`).then((r) => setRoles(r.data)); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = () => api.get(`/roles?company_id=${companyId}`).then((r) => setRoles(r.data));
  const add = async () => {
    if (!name.trim() || !companyId) return;
    try {
      await api.post("/admin/roles", { name: name.trim(), company_id: companyId, score_weight: Number(weight) || 1 });
      setName(""); setWeight("1"); load(); toast.success("Role added");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Delete this role and all its options?")) { await api.delete(`/admin/roles/${id}`); load(); } };
  const startEdit = (r) => { setEditingId(r.id); setEditName(r.name); setEditWeight(String(r.score_weight ?? 1)); };
  const saveEdit = async (r) => {
    if (!editName.trim()) { toast.error("Role name is required"); return; }
    try {
      await api.put(`/admin/roles/${r.id}`, { name: editName.trim(), company_id: r.company_id, score_weight: Number(editWeight) || 1 });
      setEditingId(null); load(); toast.success("Role updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="admin-role-company"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="font-heading text-lg font-semibold mt-5 mb-3">Add Role</div>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-new-role-name"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm focus:border-blue-500 outline-none"
          placeholder="Role name (e.g. Pilot)" />
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mt-3 mb-1.5">Score weighting</label>
        <input type="number" step="0.1" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="admin-new-role-weight"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm focus:border-blue-500 outline-none" />
        <p className="text-[11px] text-slate-500 mt-1.5">Multiplies this role&apos;s total score (default 1).</p>
        <button onClick={add} data-testid="admin-add-role-btn"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="md:col-span-2 p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <div className="font-heading text-lg font-semibold mb-3">Roles</div>
        <div className="divide-y divide-slate-800">
          {roles.length === 0 && <div className="text-sm text-slate-500">No roles for this company.</div>}
          {roles.map((r) => (
            <div key={r.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`admin-role-${r.id}`}>
              {editingId === r.id ? (
                <>
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`role-rename-${r.id}`}
                    className="flex-1 px-2.5 py-1.5 rounded-md bg-slate-950 border border-slate-700 text-sm" />
                  <input type="number" step="0.1" min="0" value={editWeight} onChange={(e) => setEditWeight(e.target.value)}
                    data-testid={`role-weight-edit-${r.id}`} title="Score weighting"
                    className="w-20 px-2.5 py-1.5 rounded-md bg-slate-950 border border-slate-700 text-sm" />
                  <button onClick={() => saveEdit(r)} data-testid={`role-save-${r.id}`}
                    className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 shrink-0">
                    <Check className="h-3 w-3" /> Save
                  </button>
                  <button onClick={() => setEditingId(null)} data-testid={`role-cancel-${r.id}`}
                    className="text-xs text-slate-400 hover:text-white shrink-0">Cancel</button>
                </>
              ) : (
                <>
                  <div className="text-sm">
                    {r.name}
                    <span className="ml-2 text-[10px] font-mono-num text-slate-500" data-testid={`role-weight-${r.id}`}>×{r.score_weight ?? 1}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button onClick={() => startEdit(r)} data-testid={`role-edit-${r.id}`}
                      className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 transition-colors">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                    <button onClick={() => del(r.id)} className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OptionsTab() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [questionKeys, setQuestionKeys] = useState({ daily_questions: [], rest_period_questions: [] });
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState({ question: "", label: "", value: 0, rag: "green", order: 0 });
  const [editOpt, setEditOpt] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [qlabels, setQlabels] = useState({});
  const [restLabels, setRestLabels] = useState({});
  const saveRestLabels = async () => {
    try {
      await api.put(`/admin/companies/${companyId}/question-labels`, { labels: { ...qlabels, ...restLabels } });
      setQlabels((q) => ({ ...q, ...restLabels }));
      toast.success("Rest period questions saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [qrequired, setQrequired] = useState({});
  const [editQ, setEditQ] = useState(null);
  const [layout, setLayout] = useState([]);
  const [layoutDirty, setLayoutDirty] = useState(false);
  const [optFocus, setOptFocus] = useState(null);

  useEffect(() => {
    api.get("/companies").then((r) => { setCompanies(r.data); if (r.data[0]) setCompanyId(r.data[0].id); });
    api.get("/admin/question-keys").then((r) => setQuestionKeys(r.data));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (companyId) api.get(`/roles?company_id=${companyId}`).then((r) => { setRoles(r.data); setRoleId(r.data[0]?.id || ""); }); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = () => { if (roleId) api.get(`/admin/options?role_id=${roleId}`).then((r) => setOptions(r.data)); };
  useEffect(() => { load(); }, [roleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const allQuestions = useMemo(
    () => [...questionKeys.daily_questions, ...questionKeys.rest_period_questions,
      ...layout.filter((i) => i.kind === "question" && i.key && i.key.startsWith("custom_")).map((i) => ({ key: i.key, label: i.label }))],
    [questionKeys, layout],
  );

  const updLayout = (id, patch) => {
    setLayout((ls) => ls.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    setLayoutDirty(true);
  };
  const [activeLayoutRow, setActiveLayoutRow] = useState(null);
  const insertIdxFor = () => {
    const i = layout.findIndex((x) => x.id === activeLayoutRow);
    return i >= 0 ? i : layout.length - 1;
  };
  const insertAfter = (idx, kind) => {
    const nid = `new_${crypto.randomUUID().slice(0, 8)}`;
    const item = kind === "divider"
      ? { id: nid, kind: "divider", key: null, label: "" }
      : { id: nid, kind: "question", key: null, label: "", required: true };
    setLayout((ls) => [...ls.slice(0, idx + 1), item, ...ls.slice(idx + 1)]);
    setLayoutDirty(true);
  };
  const removeLayoutItem = (id) => {
    setLayout((ls) => ls.filter((i) => i.id !== id));
    setActiveLayoutRow((a) => (a === id ? null : a));
    setLayoutDirty(true);
  };
  const moveLayoutItem = (id, dir) => {
    setLayout((ls) => {
      const i = ls.findIndex((x) => x.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ls.length) return ls;
      const next = [...ls];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setLayoutDirty(true);
  };
  const saveLayout = async () => {
    const items = layout.map((i) => ({
      id: i.id.startsWith("new_") ? undefined : i.id,
      kind: i.kind, key: i.key || undefined, label: i.label, required: i.required,
    }));
    try {
      const { data } = await api.put(`/admin/companies/${companyId}/question-layout`, { items });
      setLayout(data.items);
      setLayoutDirty(false);
      setActiveLayoutRow(null);
      toast.success("Question layout saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const add = async () => {
    if (!roleId || !form.question || !form.label) { toast.error("Fill role, question, label"); return; }
    try {
      await api.post("/admin/options", { role_id: roleId, ...form, value: Number(form.value), order: Number(form.order) });
      setForm({ question: form.question, label: "", value: 0, rag: "green", order: 0 });
      load();
      toast.success("Option added");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/options/${id}`); load(); };

  const grouped = options.reduce((acc, o) => { (acc[o.question] = acc[o.question] || []).push(o); return acc; }, {});

  const [copyAllQ, setCopyAllQ] = useState(true);
  const [copyQuestions, setCopyQuestions] = useState([]);
  const [copyAllRoles, setCopyAllRoles] = useState(true);
  const [copyRoleIds, setCopyRoleIds] = useState([]);
  const [copyUnitIds, setCopyUnitIds] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);

  useEffect(() => {
    if (!companyId) return;
    api.get(`/org/tree?company_id=${companyId}`).then((r) => setOrgUnits(r.data.units || [])).catch(() => {});
    api.get(`/question-labels?company_id=${companyId}`).then((r) => {
      setQlabels(r.data.labels || {});
      setQrequired(r.data.required || {});
      setRestLabels({
        rest_location: r.data.labels?.rest_location || "",
        rest_quality: r.data.labels?.rest_quality || "",
      });
    }).catch(() => {});
    api.get(`/question-layout?company_id=${companyId}`).then((r) => {
      setLayout(r.data.items || []);
      setLayoutDirty(false);
    }).catch(() => {});
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRequired = async (key) => {
    const required = { ...qrequired, [key]: !(qrequired[key] !== false) };
    try {
      await api.put(`/admin/companies/${companyId}/question-labels`, { labels: qlabels, required });
      setQrequired(required);
      toast.success(required[key] ? "Question is mandatory" : "Question is optional");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleCopyQ = (key) => setCopyQuestions((qs) => qs.includes(key) ? qs.filter((k) => k !== key) : [...qs, key]);
  const toggleCopyRole = (id) => setCopyRoleIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const toggleCopyUnit = (id) => setCopyUnitIds((ids) => ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

  const doCopy = async () => {
    if (!roleId) { toast.error("Select a source role first"); return; }
    if (!copyAllRoles && copyRoleIds.length === 0 && copyUnitIds.length === 0) { toast.error("Pick at least one target"); return; }
    if (!copyAllQ && copyQuestions.length === 0) { toast.error("Pick at least one question"); return; }
    try {
      const { data } = await api.post("/admin/options/copy", {
        source_role_id: roleId,
        questions: copyAllQ ? [] : copyQuestions,
        include_all_roles: copyAllRoles,
        target_role_ids: copyAllRoles ? [] : copyRoleIds,
        target_unit_ids: copyUnitIds,
      });
      toast.success(`Copied ${data.options_copied} options to ${data.target_roles} role(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="admin-opt-company"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Role</label>
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)} data-testid="admin-opt-role"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="">Select…</option>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="question-layout-card">
        <div className="font-heading text-lg font-semibold mb-1">Questions &amp; Layout</div>
        <p className="text-xs text-slate-500 mb-4">
          Edit question text, reorder rows with the arrows, or insert a question or divider after any row (click a
          row to select it — the blue outline shows where the buttons below will insert). The layout applies to all
          roles; options are still managed per role below.
        </p>
        <div className="space-y-2">
          {layout.map((it, idx) => (
            <div key={it.id} data-testid={`layout-row-${it.id}`}
              onClick={() => setActiveLayoutRow(it.id)}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer ${
                it.kind === "divider" ? "border-dashed border-slate-600 bg-slate-950/40" : "border-slate-800 bg-slate-950/60"
              } ${activeLayoutRow === it.id ? "ring-1 ring-blue-500" : ""}`}>
              <span className="text-[10px] font-mono-num text-slate-500 w-5 shrink-0">{idx + 1}</span>
              {it.kind === "divider" ? (
                <input value={it.label} placeholder="Divider title (optional)"
                  onChange={(e) => updLayout(it.id, { label: e.target.value })}
                  data-testid={`layout-label-${it.id}`}
                  className="flex-1 px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs italic" />
              ) : (
                <>
                  <input value={it.label} onChange={(e) => updLayout(it.id, { label: e.target.value })}
                    data-testid={`layout-label-${it.id}`}
                    className="flex-1 px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-sm" />
                  <button onClick={() => updLayout(it.id, { required: it.required === false })}
                    data-testid={`layout-required-${it.id}`}
                    className={`px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-widest border transition-colors shrink-0 ${
                      it.required !== false
                        ? "bg-blue-600/15 text-blue-300 border-blue-600/40"
                        : "bg-slate-800 text-slate-400 border-slate-700"}`}>
                    {it.required !== false ? "Mandatory" : "Optional"}
                  </button>
                </>
              )}
              <div className="flex flex-col shrink-0 -my-1">
                <button title="Move up" onClick={() => moveLayoutItem(it.id, -1)} disabled={idx === 0}
                  data-testid={`layout-up-${it.id}`}
                  className="p-0.5 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors disabled:opacity-25 disabled:hover:bg-transparent">
                  <ArrowUp className="h-3 w-3" /></button>
                <button title="Move down" onClick={() => moveLayoutItem(it.id, 1)} disabled={idx === layout.length - 1}
                  data-testid={`layout-down-${it.id}`}
                  className="p-0.5 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors disabled:opacity-25 disabled:hover:bg-transparent">
                  <ArrowDown className="h-3 w-3" /></button>
              </div>
              <button title="Insert question after" onClick={() => insertAfter(idx, "question")}
                data-testid={`layout-ins-q-${it.id}`}
                className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0">
                <Plus className="h-3.5 w-3.5" /></button>
              {it.kind === "question" && it.key && (
                <button title="Edit options for this question (selected role)" onClick={() => setOptFocus(it)}
                  data-testid={`layout-opts-${it.id}`}
                  className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0">
                  <ListChecks className="h-3.5 w-3.5" /></button>
              )}
              <button title="Insert divider after" onClick={() => insertAfter(idx, "divider")}
                data-testid={`layout-ins-d-${it.id}`}
                className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0">
                <SeparatorHorizontal className="h-3.5 w-3.5" /></button>
              <button title="Remove" onClick={() => removeLayoutItem(it.id)}
                data-testid={`layout-del-${it.id}`}
                className="p-1.5 rounded text-slate-400 hover:text-rose-300 hover:bg-slate-800 transition-colors shrink-0">
                <Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <button onClick={() => insertAfter(insertIdxFor(), "question")} data-testid="layout-add-question"
            title="Insert a question after the selected row (or at the end if none selected)"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs transition-colors">
            <Plus className="h-3.5 w-3.5" /> Add question</button>
          <button onClick={() => insertAfter(insertIdxFor(), "divider")} data-testid="layout-add-divider"
            title="Insert a divider after the selected row (or at the end if none selected)"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-xs transition-colors">
            <SeparatorHorizontal className="h-3.5 w-3.5" /> Add divider</button>
          <button onClick={saveLayout} disabled={!layoutDirty} data-testid="layout-save"
            className="ml-auto px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium transition-colors disabled:opacity-50">
            Save layout</button>
        </div>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="rest-questions-card">
        <div className="font-heading text-lg font-semibold mb-1">Rest Period Questions</div>
        <p className="text-xs text-slate-500 mb-4">
          Text of the questions asked for each rest period in the daily entry. The answer options and RAG scores
          for these questions are managed per role below.
        </p>
        <div className="space-y-3 max-w-2xl">
          {(questionKeys.rest_period_questions || []).map((q) => (
            <div key={q.key}>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">
                {q.key === "rest_location" ? "Location question" : "Quality question"}
              </label>
              <input value={restLabels[q.key] ?? q.label}
                onChange={(e) => setRestLabels((r) => ({ ...r, [q.key]: e.target.value }))}
                data-testid={`rest-question-label-${q.key}`}
                className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
            </div>
          ))}
        </div>
        <button onClick={saveRestLabels} disabled={!companyId} data-testid="rest-questions-save"
          className="mt-3 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium transition-colors disabled:opacity-50">
          Save rest questions
        </button>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60">
        <div className="font-heading text-lg font-semibold mb-3">Add Option</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Question</label>
            <select value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="admin-opt-question"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="">Select…</option>
              {allQuestions.map((q) => <option key={q.key} value={q.key}>{q.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Label</label>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="admin-opt-label"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" placeholder="e.g. 8+ hours" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Value</label>
            <input type="number" step="0.5" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="admin-opt-value"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">RAG</label>
            <select value={form.rag} onChange={(e) => setForm({ ...form, rag: e.target.value })} data-testid="admin-opt-rag"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="green">Green</option>
              <option value="yellow">Yellow</option>
              <option value="amber">Amber</option>
              <option value="red">Red</option>
            </select>
          </div>
        </div>
        <button onClick={add} data-testid="admin-add-option-btn"
          className="mt-3 inline-flex items-center gap-2 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
          <Plus className="h-4 w-4" /> Add Option
        </button>
      </div>

      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="copy-options-card">
        <div className="font-heading text-lg font-semibold mb-1">Copy questions to other roles</div>
        <p className="text-xs text-slate-500 mb-4">
          Copies the selected questions&apos; options from the currently selected role. Existing options for those
          questions on target roles are replaced.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Questions to copy</div>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input type="checkbox" checked={copyAllQ} onChange={(e) => setCopyAllQ(e.target.checked)} data-testid="copy-all-questions"
                className="accent-blue-600 h-4 w-4" />
              All questions
            </label>
            {!copyAllQ && (
              <div className="space-y-1.5 ml-1">
                {allQuestions.map((q) => (
                  <label key={q.key} className="flex items-center gap-2 text-xs text-slate-300">
                    <input type="checkbox" checked={copyQuestions.includes(q.key)} onChange={() => toggleCopyQ(q.key)}
                      data-testid={`copy-q-${q.key}`} className="accent-blue-600 h-3.5 w-3.5" />
                    {q.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Targets</div>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input type="checkbox" checked={copyAllRoles} onChange={(e) => setCopyAllRoles(e.target.checked)} data-testid="copy-all-roles"
                className="accent-blue-600 h-4 w-4" />
              All roles in this company
            </label>
            {!copyAllRoles && (
              <div className="space-y-1.5 ml-1">
                {roles.filter((r) => r.id !== roleId).map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-xs text-slate-300">
                    <input type="checkbox" checked={copyRoleIds.includes(r.id)} onChange={() => toggleCopyRole(r.id)}
                      data-testid={`copy-role-${r.id}`} className="accent-blue-600 h-3.5 w-3.5" />
                    {r.name}
                  </label>
                ))}
              </div>
            )}
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mt-3 mb-1.5">Selected org units (roles of their members)</div>
            {orgUnits.length === 0 && <div className="text-xs text-slate-600">No org units defined for this company.</div>}
            <div className="space-y-1.5 ml-1">
              {orgUnits.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={copyUnitIds.includes(u.id)} onChange={() => toggleCopyUnit(u.id)}
                    data-testid={`copy-unit-${u.id}`} className="accent-blue-600 h-3.5 w-3.5" />
                  {u.name}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Action</div>
            <p className="text-xs text-slate-500 mb-3">
              Options for the selected questions are copied from the currently selected role; existing options on target roles are replaced.
            </p>
            <button onClick={doCopy} data-testid="copy-options-btn"
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm">
              <Copy className="h-4 w-4" /> Copy Options
            </button>
          </div>
        </div>
      </div>

      <EditOptionDialog opt={editOpt} orgUnits={orgUnits} onClose={() => setEditOpt(null)} onDone={() => { setEditOpt(null); load(); }} />
      <QuestionOptionsDialog qitem={optFocus} roleId={roleId}
        onClose={() => setOptFocus(null)} onDone={() => load()} />
      <QuestionLabelDialog q={editQ} companyId={companyId} qlabels={qlabels}
        onClose={() => setEditQ(null)}
        onDone={(labels) => { setEditQ(null); setQlabels(labels); }} />

      {allQuestions.map((q) => (
        <div key={q.key} className="p-5 rounded-lg border border-slate-800 bg-slate-900/60">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{q.key}</div>
              <div className="font-heading text-base font-semibold">{qlabels[q.key] || q.label}</div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => toggleRequired(q.key)} data-testid={`toggle-required-${q.key}`}
                className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border transition-colors ${
                  qrequired[q.key] !== false
                    ? "bg-blue-600/15 text-blue-300 border-blue-600/40"
                    : "bg-slate-800 text-slate-400 border-slate-700"}`}>
                {qrequired[q.key] !== false ? "Mandatory" : "Optional"}
              </button>
              <button onClick={() => setEditQ(q)} data-testid={`edit-question-${q.key}`}
                className="text-slate-500 hover:text-white transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {(grouped[q.key] || []).length === 0 && <div className="text-xs text-slate-500">No options.</div>}
            {(grouped[q.key] || []).map((o, idx) => {
              const m = RAG_META[o.rag];
              return (
                <div key={o.id}
                  draggable
                  onDragStart={() => setDragId(o.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    if (!dragId || dragId === o.id) return;
                    const group = [...(grouped[q.key] || [])];
                    const from = group.findIndex((x) => x.id === dragId);
                    const to = group.findIndex((x) => x.id === o.id);
                    if (from < 0 || to < 0) return;
                    group.splice(to, 0, group.splice(from, 1)[0]);
                    const reordered = group.map((x, i) => ({ ...x, order: i }));
                    setOptions([...options.filter((x) => x.question !== q.key), ...reordered]);
                    setDragId(null);
                    try {
                      await api.post("/admin/options/reorder", { ordered_ids: group.map((x) => x.id) });
                      toast.success("Order saved");
                    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); load(); }
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-2 cursor-grab active:cursor-grabbing ${m.cls} ${dragId === o.id ? "opacity-40" : ""}`}
                  data-testid={`admin-option-${o.id}`}>
                  <GripVertical className="h-3 w-3 opacity-60" />
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${m.dot}`} />
                  <span>{o.label}</span>
                  <span className="font-mono-num opacity-80">({o.value})</span>
                  <span className="font-mono-num opacity-50 text-[10px]">#{idx + 1}</span>
                  <button onClick={() => setEditOpt(o)} data-testid={`admin-option-edit-${o.id}`} className="ml-1 opacity-70 hover:opacity-100"><Pencil className="h-3 w-3" /></button>
                  <button onClick={() => del(o.id)} className="opacity-70 hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function QuestionOptionsDialog({ qitem, roleId, onClose, onDone }) {
  const [opts, setOpts] = useState([]);
  const [newOpt, setNewOpt] = useState({ label: "", value: 0, rag: "green" });
  const qkey = qitem?.key;
  const load = () => {
    if (qkey && roleId) api.get(`/admin/options?role_id=${roleId}`).then((r) => setOpts(r.data.filter((o) => o.question === qkey))).catch(() => {});
  };
  useEffect(() => { setOpts([]); load(); }, [qkey, roleId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!qitem) return null;
  const update = (id, patch) => setOpts((os) => os.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  const saveOpt = async (o) => {
    try {
      await api.put(`/admin/options/${o.id}`, { role_id: roleId, question: qkey, label: o.label, value: Number(o.value), rag: o.rag, order: Number(o.order) });
      toast.success("Option saved");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delOpt = async (id) => {
    await api.delete(`/admin/options/${id}`);
    load(); onDone();
  };
  const addOpt = async () => {
    if (!newOpt.label.trim()) { toast.error("Enter an option label"); return; }
    try {
      await api.post("/admin/options", { role_id: roleId, question: qkey, label: newOpt.label.trim(), value: Number(newOpt.value), rag: newOpt.rag, order: opts.length });
      setNewOpt({ label: "", value: 0, rag: "green" });
      load(); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!qitem} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl" data-testid="question-options-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Options — {qitem.label}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 mb-3">Editing options for the currently selected role.</p>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {opts.length === 0 && <div className="text-xs text-slate-500">No options yet for this question.</div>}
          {opts.map((o) => (
            <div key={o.id} className="flex items-center gap-2" data-testid={`qopt-row-${o.id}`}>
              <input value={o.label} onChange={(e) => update(o.id, { label: e.target.value })} data-testid={`qopt-label-${o.id}`}
                className="flex-1 px-2 py-1.5 rounded bg-slate-950 border border-slate-800 text-sm" />
              <input type="number" step="0.5" value={o.value} onChange={(e) => update(o.id, { value: e.target.value })} data-testid={`qopt-value-${o.id}`}
                className="w-20 px-2 py-1.5 rounded bg-slate-950 border border-slate-800 text-sm" />
              <select value={o.rag} onChange={(e) => update(o.id, { rag: e.target.value })} data-testid={`qopt-rag-${o.id}`}
                className="px-2 py-1.5 rounded bg-slate-950 border border-slate-800 text-sm">
                <option value="green">Green</option><option value="yellow">Yellow</option>
                <option value="amber">Amber</option><option value="red">Red</option>
              </select>
              <button onClick={() => saveOpt(o)} title="Save option" data-testid={`qopt-save-${o.id}`}
                className="p-1.5 rounded text-emerald-400 hover:bg-slate-800 transition-colors"><Check className="h-3.5 w-3.5" /></button>
              <button onClick={() => delOpt(o.id)} title="Delete option" data-testid={`qopt-del-${o.id}`}
                className="p-1.5 rounded text-rose-400 hover:bg-slate-800 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-800">
          <input value={newOpt.label} onChange={(e) => setNewOpt({ ...newOpt, label: e.target.value })} placeholder="New option label"
            data-testid="qopt-new-label" className="flex-1 px-2 py-1.5 rounded bg-slate-950 border border-slate-800 text-sm" />
          <input type="number" step="0.5" value={newOpt.value} onChange={(e) => setNewOpt({ ...newOpt, value: e.target.value })}
            data-testid="qopt-new-value" className="w-20 px-2 py-1.5 rounded bg-slate-950 border border-slate-800 text-sm" />
          <select value={newOpt.rag} onChange={(e) => setNewOpt({ ...newOpt, rag: e.target.value })} data-testid="qopt-new-rag"
            className="px-2 py-1.5 rounded bg-slate-950 border border-slate-800 text-sm">
            <option value="green">Green</option><option value="yellow">Yellow</option>
            <option value="amber">Amber</option><option value="red">Red</option>
          </select>
          <button onClick={addOpt} data-testid="qopt-add-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-xs shrink-0">
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditOptionDialog({ opt, orgUnits = [], onClose, onDone }) {
  const [form, setForm] = useState({ label: "", value: 0, rag: "green", order: 0 });
  const [scopeMode, setScopeMode] = useState("role");
  const [unitIds, setUnitIds] = useState([]);
  useEffect(() => {
    if (opt) {
      setForm({ label: opt.label, value: opt.value, rag: opt.rag, order: opt.order ?? 0 });
      setScopeMode("role");
      setUnitIds([]);
    }
  }, [opt]);
  if (!opt) return null;
  const submit = async () => {
    if (scopeMode === "units" && unitIds.length === 0) { toast.error("Pick at least one org unit"); return; }
    try {
      const { data } = await api.put(`/org/options/${opt.id}`, {
        label: form.label, value: Number(form.value), rag: form.rag, order: Number(form.order),
        apply_all_roles: scopeMode === "all",
        apply_unit_ids: scopeMode === "units" ? unitIds : [],
      });
      toast.success(data.propagated ? `Saved — applied to ${data.propagated} matching option(s) in scope` : "Option updated");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!opt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="edit-option-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit option</DialogTitle>
        </DialogHeader>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Answer text</label>
        <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="edit-option-label"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm mb-3" />
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Value</label>
            <input type="number" step="0.5" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="edit-option-value"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Colour</label>
            <select value={form.rag} onChange={(e) => setForm({ ...form, rag: e.target.value })} data-testid="edit-option-rag"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
              <option value="green">Green</option>
              <option value="yellow">Yellow</option>
              <option value="amber">Amber</option>
              <option value="red">Red</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Order</label>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="edit-option-order"
              className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Apply changes to</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="opt-scope" checked={scopeMode === "role"} onChange={() => setScopeMode("role")}
                data-testid="edit-scope-role" className="accent-blue-600" />
              This role only
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="opt-scope" checked={scopeMode === "all"} onChange={() => setScopeMode("all")}
                data-testid="edit-scope-all" className="accent-blue-600" />
              All roles in this company
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" name="opt-scope" checked={scopeMode === "units"} onChange={() => setScopeMode("units")}
                data-testid="edit-scope-units" className="accent-blue-600" />
              Roles of selected org units
            </label>
            {scopeMode === "units" && (
              <div className="ml-6 space-y-1.5">
                {orgUnits.length === 0 && <div className="text-xs text-slate-600">No org units defined.</div>}
                {orgUnits.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-xs text-slate-300">
                    <input type="checkbox" checked={unitIds.includes(u.id)}
                      onChange={() => setUnitIds((ids) => ids.includes(u.id) ? ids.filter((x) => x !== u.id) : [...ids, u.id])}
                      data-testid={`edit-scope-unit-${u.id}`} className="accent-blue-600 h-3.5 w-3.5" />
                    {u.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Scoped application matches the same question and position on each target role.</p>
        </div>
        <button onClick={submit} data-testid="edit-option-save-btn"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save option</button>
      </DialogContent>
    </Dialog>
  );
}

function QuestionLabelDialog({ q, companyId, qlabels, onClose, onDone }) {
  const [text, setText] = useState("");
  useEffect(() => { if (q) setText(qlabels[q.key] || q.label); }, [q, qlabels]);
  if (!q) return null;
  const submit = async () => {
    if (!text.trim()) { toast.error("Enter question text"); return; }
    try {
      const labels = { ...qlabels, [q.key]: text.trim() };
      await api.put(`/admin/companies/${companyId}/question-labels`, { labels });
      toast.success("Question text updated for this company");
      onDone(labels);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!q} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="edit-question-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit question text</DialogTitle>
        </DialogHeader>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1">{q.key}</div>
        <input value={text} onChange={(e) => setText(e.target.value)} data-testid="edit-question-text"
          className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm" />
        <p className="text-[11px] text-slate-500 mt-2">Applies to every role in this company.</p>
        <button onClick={submit} data-testid="edit-question-save-btn"
          className="mt-4 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium">Save question text</button>
      </DialogContent>
    </Dialog>
  );
}
